import axios from 'axios';
import config from '../config';
import { getBearerToken } from '../util/cookie';

// Coach resource archive — reusable documents/videos a coach stores
// once and shares with clients (per-resource sharing). The actual file
// upload to Firebase Storage happens on the PAGE (via AddFilePopup)
// BEFORE these thunks are called; here we only persist/read metadata.

// ================ Action types ================ //

export const RESOURCES_REQUEST = 'app/CoachSaaS/RESOURCES_REQUEST';
export const RESOURCES_SUCCESS = 'app/CoachSaaS/RESOURCES_SUCCESS';
export const RESOURCES_ERROR = 'app/CoachSaaS/RESOURCES_ERROR';

export const CLIENT_RESOURCES_REQUEST = 'app/CoachSaaS/CLIENT_RESOURCES_REQUEST';
export const CLIENT_RESOURCES_SUCCESS = 'app/CoachSaaS/CLIENT_RESOURCES_SUCCESS';
export const CLIENT_RESOURCES_ERROR = 'app/CoachSaaS/CLIENT_RESOURCES_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Coach side
  resources: [],
  tier: null,
  limits: null,
  usage: null,
  resourcesFetching: false,
  resourcesError: null,
  // Client side
  clientResources: [],
  clientResourcesFetching: false,
  clientResourcesError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case RESOURCES_REQUEST:
      return { ...state, resourcesFetching: true, resourcesError: null };
    case RESOURCES_SUCCESS:
      return {
        ...state,
        resourcesFetching: false,
        resources: payload.resources || [],
        tier: payload.tier || null,
        limits: payload.limits || null,
        usage: payload.usage || null,
      };
    case RESOURCES_ERROR:
      return { ...state, resourcesFetching: false, resourcesError: payload };
    case CLIENT_RESOURCES_REQUEST:
      return { ...state, clientResourcesFetching: true, clientResourcesError: null };
    case CLIENT_RESOURCES_SUCCESS:
      return { ...state, clientResourcesFetching: false, clientResources: payload };
    case CLIENT_RESOURCES_ERROR:
      return { ...state, clientResourcesFetching: false, clientResourcesError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const resourcesRequest = () => ({ type: RESOURCES_REQUEST });
export const resourcesSuccess = d => ({ type: RESOURCES_SUCCESS, payload: d });
export const resourcesError = e => ({ type: RESOURCES_ERROR, payload: e });

export const clientResourcesRequest = () => ({ type: CLIENT_RESOURCES_REQUEST });
export const clientResourcesSuccess = d => ({ type: CLIENT_RESOURCES_SUCCESS, payload: d });
export const clientResourcesError = e => ({ type: CLIENT_RESOURCES_ERROR, payload: e });

// ================ Thunks (coach) ================ //

export const fetchMyResources = () => async (dispatch, getState, sdk) => {
  dispatch(resourcesRequest());
  try {
    const res = await axios.post(
      `${config.coachSaaSService}resources/list`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          Authorization: await getBearerToken(sdk),
        },
      }
    );
    dispatch(resourcesSuccess(res.data || {}));
    return res.data;
  } catch (e) {
    dispatch(resourcesError(e));
    throw e;
  }
};

// Create a resource. `payload` carries the AddFilePopup metadata
// (url, type, name, size…) plus archive fields (description, category,
// tags, sharingScope, sharedClientIds). Throws on 403 tier-cap so the
// page can read err.response.data.{error,limit,current} and show the
// right message.
export const createResource = payload => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}resources/create`,
    { payload },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const updateResource = (resourceId, payload) => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}resources/update`,
    { resourceId, payload },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const deleteResource = resourceId => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}resources/delete`,
    { resourceId },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

// ================ Thunks (client) ================ //

export const fetchClientResources = () => async (dispatch, getState, sdk) => {
  dispatch(clientResourcesRequest());
  try {
    const res = await axios.post(
      `${config.coachSaaSService}client/resources/list`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          Authorization: await getBearerToken(sdk),
        },
      }
    );
    dispatch(clientResourcesSuccess((res.data && res.data.resources) || []));
    return res.data;
  } catch (e) {
    dispatch(clientResourcesError(e));
    throw e;
  }
};
